import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthForm } from "@/components/auth/AuthForm";
import { useAuth } from "@/components/auth/AuthProvider";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import { redeemPendingReferral } from "@/hooks/useReferral";
import { Lock } from "lucide-react";

export default function Auth() {
  const { user, loading } = useAuth();
  const { settings } = usePublicSettings();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const wasLoggedOut = useRef(!user);

  // Determine if this is a signup flow
  const isSignupRoute = location.pathname === "/signup";

  // Sanitize the ?redirect= target — only same-origin paths starting
  // with a single "/". Without this, an attacker can mail
  // `/login?redirect=https%3A%2F%2Fevil.com` and we'd happily forward
  // there after a successful login (open-redirect → phishing).
  const rawRedirect = searchParams.get("redirect");
  const redirectTo =
    rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") && !rawRedirect.includes(":")
      ? rawRedirect
      : "/dashboard";

  useEffect(() => {
    if (!loading && user) {
      // If user just signed up (was logged out, now logged in, and on signup route)
      // redirect with activation param
      if (wasLoggedOut.current && isSignupRoute) {
        // Fire-and-forget referral redemption. Failure doesn't block
        // the signup flow — the user might just not have a ?ref=
        // pending, which returns false silently.
        redeemPendingReferral().catch(() => {});
        navigate("/dashboard?activation=new", { replace: true });
      } else {
        navigate(redirectTo, { replace: true });
      }
    }
    // Update ref for next render
    if (!loading) {
      wasLoggedOut.current = !user;
    }
  }, [user, loading, navigate, redirectTo, isSignupRoute]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  // Admin can flip `signups_enabled` to false in /admin/settings to
  // pause new account creation (e.g. during a maintenance window or a
  // viral spike). Existing users can still log in.
  const signupsBlocked =
    isSignupRoute &&
    settings &&
    (settings as { signups_enabled?: boolean }).signups_enabled === false;

  if (signupsBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full glass-card rounded-2xl shadow-lg p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-warn/10 grid place-items-center">
            <Lock className="w-6 h-6 text-warn" />
          </div>
          <h1 className="text-xl font-semibold text-text mb-2">
            {t("auth.signups_paused_title")}
          </h1>
          <p className="text-sm text-muted mb-4">
            {t("auth.signups_paused_body")}
          </p>
          <Link to="/login" className="text-sm text-brand font-medium hover:underline">
            {t("auth.have_account_login")}
          </Link>
        </div>
      </div>
    );
  }

  return <AuthForm />;
}
